import React, { useContext, useEffect, useMemo, useState } from "react";
// import { Chart } from "react-chartjs-2";
// import { tableFromIPC } from '@uwdata/flechette';

// import { Select } from "./input";
// import { BarChart } from '@mui/x-charts/BarChart';
import numeral from "numeral";
import Plot from "react-plotly.js";
import { getHistogramData, HistogramData } from "../api/data";
import { ExploreContext } from "../context/explore";
import { ExploreSchema } from "../core/global";
import { getExploreColumnKeys } from "../helpers/core";
import { WarningMessage } from "./error-message";
import { ExploreSelect } from "./explore-select";
import Loader from "./loader";

interface ExecProps {
  preview: boolean;
  filename: string;
  vector: string;
  orderId: string | number | null;
  provider: string | null;
  model: string | null;
  geography: string | null;
  version: string | null;
}

interface ExploreHistogramPlotProps {
  exploreDataSchema: ExploreSchema;
  loading: boolean;
}
export const ExploreHistogramPlot = ({
  exploreDataSchema,
  loading,
}: ExploreHistogramPlotProps) => {
  const [keyOne, setKeyOne] = useState<string | null>(null);
  const [error, setError] = useState<string[] | null>(null)
  const [dataLoading, setDataLoading] = useState<boolean>(false)
  const [data, setData] = useState<HistogramData | null>(null)
  const [units, setUnits] = useState<string>('')

  const { 
    isPurchasedPoint,
  } = useContext(ExploreContext);

  useEffect(() => {
    setDataLoading(false)
    setError(null)
    const execute = async ({
      preview,
      filename,
      vector,
      ...props
    }: ExecProps) => {
      setDataLoading(true)
      const result = await getHistogramData({
        filename,
        preview,
        vector,
        ...props
      })
      const {error, response} = result

      if (error === 200 && response) {
        const useData = {...response} as HistogramData
        const {distribution, ticks, units} = useData
        if (distribution && Array.isArray(distribution) && ticks && Array.isArray(ticks) && units) {
          setData({
            distribution,
            ticks,
            units
          })
          setUnits(units)
        } else {
          setData(null)
          setUnits('')
          setError([`Problem loading histogram data for **${keyOne}**.`])
        }
      } else {
        setError([`Problem loading histogram data for **${keyOne}**.`])
      }
      setDataLoading(false)

    }
    if (exploreDataSchema && keyOne) {
      const {
        description: { 
          model = null, 
          provider = null, 
          geography = null, 
          version = null, 
          filename, 
          uid, 
          orderId = null,
        },
      } = exploreDataSchema;

      // const [provider = '', model = '', geography = '', version = null] = modelKey.split('-')

      const preview = !!(uid && `${uid}`.indexOf('preview') !== -1)
      const vector = keyOne || ''
      execute({
        preview, 
        filename, 
        vector,
        provider,
        model,
        geography,
        version,
        orderId,
      })
    }
  }, [exploreDataSchema, keyOne]);


  const [latStr, lonStr] = useMemo(() => {
    if (exploreDataSchema !== null) {
      const {description: {
        lat,
        lon
      }} = exploreDataSchema;
    
      const latStr = numeral(lat).format("0.00000");
      const lonStr = numeral(lon).format("0.00000");
      return [latStr, lonStr]
    }
    return ["", ""];
  },[JSON.stringify(exploreDataSchema)])
  
  const {variableOptions, title} = useMemo(() => {
    let variableOptions: any[] = []
    let title = ''
    if (exploreDataSchema) {
      const {
        vectors,
      } = exploreDataSchema;
      
      variableOptions = getExploreColumnKeys(
        exploreDataSchema,
        'all'
      )
      const found = vectors.find((v: any) => v.key === keyOne)
      if (found) {
        const {name} = found
        title = name
      } else {
        title = ''
      }
    }
    return {
      variableOptions,
      title,
    };
  }, [exploreDataSchema, keyOne]);
  
  const isLoading =
    dataLoading ||
    exploreDataSchema === null ||
    keyOne === null ||
    loading;


  return (
    <div style={{ height: "550px", width: "100%" }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: "16px" }}>
        <ExploreSelect
          title="Variable"
          columnKeys={variableOptions || []}
          onChangeKey={(key) => {
            if (key && Array.isArray(key)) {
              setKeyOne(key[0]);
            } else {
              setKeyOne(null);
            }
          }}
        />
      </div>
      {isLoading ? (
        <div style={{ width: "100%", padding: "240px 0 64px 0" }}>
          <Loader center showCog />
        </div>
      ) : (
        <>
          <div
            style={{
              height: "550px",
              width: "100%",
              textAlign: "center",
            }}
          >
            {error ? 
              <WarningMessage message={error} />
            :
              data && <Plot
                config={{
                  modeBarButtonsToRemove: [ "lasso2d", "select2d", "sendDataToCloud", "zoom2d", "pan2d", "zoomIn2d", "zoomOut2d", "autoScale2d", "resetScale2d", "hoverClosestCartesian", "hoverCompareCartesian", "zoom3d", "pan3d", "orbitRotation", "tableRotation", "handleDrag3d", "resetCameraDefault3d", "resetCameraLastSave3d", "hoverClosest3d", "zoomInGeo", "zoomOutGeo", "resetGeo", "hoverClosestGeo", "hoverClosestGl2d", "hoverClosestPie", "toggleHover", "resetViews", "toggleSpikelines", "zoomInMapbox", "zoomOutMapbox", "resetViewMapbox", "togglespikelines", "togglehover", "hovercompare", "hoverclosest", "v1hovermode"],
                  toImageButtonOptions: {
                    format: "png",
                    filename: `${(keyOne || '').toLowerCase().replace(/\s/gi, "-")}${latStr}-lon${lonStr}`,
                    height: 600,
                    width: 800,
                    scale: 6,
                  },
                }}
                data={[
                  {
                    x: data.ticks,
                    y: data.distribution,
                    type: 'bar'
                  }]
                }
                layout={{
                  images: isPurchasedPoint ? undefined : [{
                    source: "/mow-logo-inline-full.png",
                    xref: "paper",
                    yref: "paper",
                    x: -0.05,
                    y: -0.15,
                    xanchor: "left",
                    yanchor: "bottom",
                    sizex: 0.3,
                    sizey: 0.3,
                    // sizing: "contain",
                    opacity: 1,
                    layer: "above"
                  },],
                  height: 600,
                  bargap: 0,
                  title: {
                    text: `${title} (${units}) at ${latStr}, ${lonStr}`,
                  },
                  // font: { size: 16 },
                  yaxis: {
                    fixedrange: true,
                    showdividers: true,
                    showline: true,
                    showgrid: true,
                    showticklabels: true,
                    tickformat: `,.2r`,
                    title: {
                      text: "Count",
                      position: "middle center",
                    }
                  },
                  xaxis: {
                    fixedrange: true,
                    showgrid: true,
                    title: {
                      text: units,
                      position: "middle center",
                    },
                }
                }}
              />
            }
          </div>
        </>
      )}
    </div>
  );
}